import { Autocomplete, Box, Button, Grid, InputLabel, OutlinedInput, Stack } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { cacheService, userService } from "../../../Core/Api/services";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalCreateConversationElementProps = {
  onClose?: (props: CloseClbProps) => void;
  users?: DbUserBasicInfos[];
  groupName?: string;
  groupId?: number;
  onClickConfirmModal: (isCreation: boolean, usersSelected: DbUserBasicInfos[], name: string, groupId?: number) => void;
};
export type ModalCreateConversationProps = CommonModalProps<ModalCreateConversationElementProps>;

const ModalCreateConversation = React.forwardRef<any, ModalCreateConversationProps>((props, ref) => {
  const { closeModal, onClose, users, groupName, groupId, onClickConfirmModal, ...modalProps } = props;
  const { t } = useTranslation();

  const [usersSelected, setUsersSelected] = React.useState<DbUserBasicInfos[]>([]);
  const [name, setName] = React.useState<string>("");
  const [usersAvailable, setUsersAvailable] = React.useState<DbUserBasicInfos[]>([]);
  const [isCreation, setIsCreation] = React.useState<boolean>(true);

  React.useEffect(() => {
    setUsersSelected(users || []);
    setName(groupName || "");
    setIsCreation(groupId === undefined);
  }, [users, groupName, groupId]);

  const handleConfirmClick = () => {
    onClickConfirmModal(isCreation, usersSelected, name, groupId);
    onClose && onClose({ confirm: true });
    closeModal();
  };

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const onChangeSearchEmail = (entry: string) => {
    if (entry === "" || entry === undefined || entry === null) {
      setUsersAvailable([]);
      return;
    }
    userService.SearchUsersBasic(entry).then(function (response) {
      if (response.success && response.users)
        setUsersAvailable(response.users.filter((user) => user.id !== cacheService.GetUserInfo()?.id));
    });
  };

  return (
    <Modal
      {...modalProps}
      title={isCreation ? t("modal:title.createConversation") : t("modal:title.updateConversation")}
      reference={ref}
      closeModal={handleCancelClick}
      width={504}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={usersSelected.length === 0}
          >
            {isCreation ? t("button:createAndTalk") : t("common:update")}
          </Button>
        </Stack>
      }
    >
      <Grid container>
        <Grid item xs={12} mb={3}>
          <InputLabel htmlFor="searchEmail" sx={{ mb: 1 }}>
            {t("field:label.addUsersToConversation")}
          </InputLabel>
          <Autocomplete
            id="searchEmail"
            options={usersAvailable.sort((a, b) => {
              if ((a.firstName ?? "") < (b.firstName ?? "")) return -1;
              if ((a.firstName ?? "") > (b.firstName ?? "")) return 1;
              if ((a.lastName ?? "") < (b.lastName ?? "")) return -1;
              if ((a.lastName ?? "") > (b.lastName ?? "")) return 1;
              return 0;
            })}
            onChange={(e, value) => {
              setUsersSelected(value);
            }}
            popupIcon={<ExpandMoreRoundedIcon />}
            getOptionLabel={(option) => option.name!}
            isOptionEqualToValue={(option, value) => value === option}
            value={usersSelected}
            renderOption={(props, option) => {
              return (
                <Box {...props} key={option.id} component="li">
                  {option.name}
                </Box>
              );
            }}
            multiple
            renderInput={(params) => (
              <OutlinedInput
                {...params.InputProps}
                inputProps={params.inputProps}
                onChange={(e) => onChangeSearchEmail(e.target.value)}
                placeholder={t("field:placeholder.typeFirstOrLastName")}
                required
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <InputLabel sx={{ mb: 1 }}>{t("common:name")}</InputLabel>
          <OutlinedInput
            id={"name"}
            sx={{ width: "100%" }}
            onChange={(e) => setName(e.currentTarget.value)}
            value={name || ""}
            placeholder={t("field:placeholder.conversationName")}
          />
        </Grid>
      </Grid>
    </Modal>
  );
});

export default ModalCreateConversation;
